<script setup lang="ts">
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      date: string
      isDirect?: boolean
      noFlight?: boolean
      isCheapestFlight?: boolean
      isInRange?: boolean
      isStart?: boolean
      isEnd?: boolean
      disabled?: boolean
      price?: null | number
      isInbound?: boolean
      noData?: boolean
      hidePrices?: boolean
      emptyCellContent?: boolean
    }>(),
    {
      isDirect: false,
      noFlight: false,
      isCheapestFlight: false,
      isInRange: false,
      isStart: false,
      isEnd: false,
      disabled: false,
      price: null,
      isInbound: false,
      noData: false,
      hidePrices: false,
      emptyCellContent: false
    }
  )
  const { isDesktop, isMobile } = useBTBreakpoints()
  const { t } = useI18n()

  const isSelected = computed(() => props.isStart || props.isEnd)
  const isUnavailableFlight = computed(() => props.noFlight && !props.noData)
  const visiblePrice = computed(() => (props.price && !props.hidePrices ? props.price : null))
  const showDirectFlightIcon = computed(() => !props.noData && props.isDirect)
  const showCellContent = computed(() => !props.emptyCellContent && (!props.noFlight || props.noData))
  const showFlightIcon = computed(() => isUnavailableFlight.value || showDirectFlightIcon.value)

  const cellBase = cva('cell flex flex-col p-1 sm:p-2 size-full sm:justify-start', {
    variants: {
      isCheapestFlight: {
        true: 'bg-[#f9fae5]',
        false: ''
      },
      isSelected: {
        true: '!bg-calendar-day-selected',
        false: ''
      },
      isStartDate: {
        true: 'date-start',
        false: ''
      },
      isEndDate: {
        true: 'date-end',
        false: ''
      },
      hasHover: {
        true: 'hover:bg-calendar-day-selected',
        false: ''
      },
      isStartAndEndDate: {
        true: 'date-start-and-end hover:start-and-end',
        false: ''
      },
      noFlight: {
        true: 'cursor-auto',
        false: 'cursor-pointer'
      },
      hasTooltip: {
        true: 'tooltip',
        false: ''
      }
    }
  })

  const dateBase = cva('text-2xs leading-none md:text-xs', {
    variants: {
      isUnavailableFlight: {
        true: '!text-border',
        false: ''
      }
    }
  })

  const base = computed(() =>
    cellBase({
      isCheapestFlight: props.isCheapestFlight,
      isSelected: isSelected.value || props.isInRange,
      isStartDate: props.isStart,
      isEndDate: props.isEnd,
      hasHover: !props.disabled && (!props.noFlight || props.noData),
      isStartAndEndDate: props.isStart && props.isEnd,
      noFlight: isUnavailableFlight.value,
      hasTooltip: isDesktop.value
    })
  )

  const dateClass = computed(() =>
    dateBase({
      isUnavailableFlight: isUnavailableFlight.value
    })
  )

  const tooltipFlightIsDirect = computed(() => {
    return t(`fsf.calendar.cellContent.tooltip.${props.isDirect ? 'direct' : 'connecting'}`)
  })

  const tooltipFlightDirection = computed(() => {
    return t(`fsf.calendar.cellContent.tooltip.${props.isInbound ? 'inbound' : 'outbound'}`)
  })

  const tooltipNoPrice = computed(() => {
    return t('fsf.calendar.cellContent.tooltip.noPrice')
  })

  const tooltipContent = computed(() => {
    if (props.noData) {
      return tooltipNoPrice.value
    }

    if (props.noFlight) {
      return t('fsf.calendar.cellContent.tooltip.noFlight')
    }

    if (props.price) {
      return `${tooltipFlightIsDirect.value} ${tooltipFlightDirection.value}`
    } else {
      return `${tooltipFlightIsDirect.value} ${tooltipNoPrice.value}`
    }
  })

  const iconSize = computed(() => (isDesktop.value ? 'sm' : 'xs'))
  const loadingIconSize = computed(() => (isMobile.value ? 'md' : 'lg'))
</script>

<template>
  <button
    :class="base"
    :disabled="disabled"
    :tabindex="noFlight && !noData ? -1 : 0"
    type="button"
  >
    <span
      v-if="isDesktop"
      class="tooltip__container"
    >
      {{ tooltipContent }}
    </span>
    <div class="flex h-4 w-full shrink-0 items-center justify-between sm:h-5">
      <div :class="dateClass">
        {{ date }}
      </div>

      <div
        v-if="showFlightIcon"
        class="flex size-4 items-center justify-center sm:size-5"
      >
        <Icon
          v-if="isUnavailableFlight"
          name="fsf/airplane-inactive"
          :size="iconSize"
          class="text-border"
        />
        <Icon
          v-else-if="showDirectFlightIcon"
          name="common/round-airplane"
          :size="iconSize"
          class="text-bt-dark-blue rotate-90"
        />
      </div>
    </div>
    <div
      v-if="showCellContent"
      class="flex min-h-0 w-full flex-1 items-center justify-center"
    >
      <template v-if="noData">
        <div class="flex w-full justify-center text-secondary-light">
          <Icon
            name="common/load-euro"
            :size="loadingIconSize"
          />
        </div>
      </template>

      <template v-else-if="!noFlight">
        <div
          v-if="visiblePrice"
          class="flex w-full justify-center font-medium"
        >
          <FsfPrice
            :price="visiblePrice"
            :show-plus="isInbound"
            increase-font-size-on-desktop
          />
        </div>
        <div
          v-else
          class="flex w-full justify-center text-secondary-light"
        >
          <Icon
            name="common/load-euro"
            :size="loadingIconSize"
          />
        </div>
      </template>
    </div>
  </button>
</template>

<style lang="scss" scoped>
  $leftLine: inset 2px 0 theme('colors.bt.blue.ui');
  $rightLine: inset -2px 0 theme('colors.bt.blue.ui');

  .date {
    &-start {
      box-shadow: $leftLine;
    }
    &-end {
      box-shadow: $rightLine;
    }
    &-start-and-end {
      box-shadow: $leftLine, $rightLine;
    }
  }

  .tooltip {
    @apply relative;

    &__container {
      @apply invisible absolute bottom-full left-1/2 z-20 mb-2 w-64 -translate-x-1/2 rounded bg-white px-3 py-2 text-center drop-shadow-md transition-opacity duration-300 text-body-2;

      &:after {
        content: '';
        @apply absolute left-1/2 top-full -translate-x-1/2 border-8 border-solid border-transparent border-t-white;
      }
    }

    &:hover {
      .tooltip__container {
        @apply visible opacity-100;
      }
    }
  }
</style>
